import { tl } from './i18nService';
import { parseParametersString } from '../util/utils';
import { Conditions, Filter } from '../store/common/search/types';
import {
    Categories, TabData, TabInfo, TabItem, TabSetting,
} from '../types/TabTypes';
import { getAppSpecifiedNavigationListService } from './commonNavigationService';
import { FeatureNamespace } from './featureService';

export const tabStateBuilder = {
    loadCustomTabs(customTabs: any[], previousStateTabs: TabInfo[], dataType: string, defaultFilters: string[]) {
        let tabs = this.getLocalTabsDefinitions(dataType, defaultFilters);
        // sorting in chronological order to preserve order in the UI
        customTabs.sort((a: TabInfo, b: TabInfo) => a.filterId - b.filterId);
        customTabs.forEach((tabResponse: any) => {
            const tab: TabData = parseParametersString(tabResponse.parameters);
            const localTabsIndex: number = tabs.findIndex((tabInfo: TabInfo) => tab.sourceId && tabInfo.tab.id === tab.sourceId);
            if (localTabsIndex > -1 && tab.category === Categories.OVERRIDE) {
                tab.id = tabs[localTabsIndex].tab.id;
                tabs = Object.assign([], tabs, { [localTabsIndex]: { tab, filterId: tabResponse.filterId, isTitleEditable: false } });
            } else {
                tab.id = tabResponse.filterId.toString();
                const existingTab: TabInfo = previousStateTabs && previousStateTabs.length
                    ? this.getTabInfoById(previousStateTabs, tab.id) : undefined;
                const alreadyEditingTitle = existingTab ? existingTab.isTitleEditable : false;
                tabs.push({ tab, isTitleEditable: alreadyEditingTitle, filterId: tabResponse.filterId });
            }
        });
        return tabs;
    },
    resetTab(tabs: TabInfo[], tabId: string): TabInfo[] {
        const appSpecifiedNavigationListService = getAppSpecifiedNavigationListService();
        const newTabs: TabInfo[] = [...tabs];
        const index = tabs.findIndex((tabInfo: TabInfo) => tabInfo.tab.id === tabId);
        const defaultTabs: any[] = appSpecifiedNavigationListService.getDefaultTabsForDataType(newTabs[index].tab.dataType);
        const tab = defaultTabs.find((tab: TabSetting) => tab.id === tabId);
        return Object.assign([], newTabs, { [index]: { tab, isTitleEditable: false } });
    },
    removeTab(tabsData: TabInfo[], selectedTab: string) {
        return tabsData.filter((tabInfo: TabInfo) => tabInfo.tab.id !== selectedTab);
    },
    cloneTab(tabs: TabInfo[], tabId: string, newTabId: string, newName: string): TabInfo[] {
        const index: number = tabs.findIndex((tabInfo: TabInfo) => tabInfo.tab.id === tabId);
        const newTab: TabInfo = {
            ...tabs[index],
            tab: {
                ...tabs[index].tab,
                id: newTabId, // need a new temporaryId
                name: newName,
                category: Categories.CUSTOM,
            },
        };
        return [...tabs, newTab];
    },
    changeTabId(tabs: TabInfo[], oldTabId: string, newTabId: string) {
        const newTabs: TabInfo[] = [...tabs];
        const tabIndex: number = tabs.findIndex((tabInfo: TabInfo) => tabInfo.tab.id === oldTabId);
        const modifiedTab: TabInfo = {
            ...tabs[tabIndex],
            tab: {
                ...tabs[tabIndex].tab,
                id: newTabId,
            },
        };
        newTabs[tabIndex] = modifiedTab;
        return newTabs;
    },
    getFiltersCount(tabs: TabInfo[], tabId: string) {
        if (tabs.length && tabId) {
            const tabIndex: number = tabs.findIndex((tabInfo: TabInfo) => tabInfo.tab.id === tabId);
            return tabs[tabIndex].tab.filters.filter((filter: Filter) => !filter.tmp).length;
        }
        return undefined;
    },
    addNewTab(tabs: TabInfo[], tabId: string, tabName: string, allTabId: string) {
        const newTabs: TabInfo[] = [...tabs];
        const allDataTab = newTabs.find((element) => {
            return element.tab.id === allTabId;
        });
        const newTab: TabInfo = {
            ...allDataTab,
            tab: {
                ...allDataTab.tab,
                id: tabId, // a new temporaryId
                name: tabName,
                category: Categories.CUSTOM,
            },
            isTitleEditable: false,
        };
        newTabs.push(newTab);
        return newTabs;
    },
    changeTabName(tabs: TabInfo[], tabId: string, newName: string) {
        const newTabs: TabInfo[] = [...tabs];
        const tabIndex: number = tabs.findIndex((tabInfo: TabInfo) => tabInfo.tab.id === tabId);
        newTabs[tabIndex] = {
            ...tabs[tabIndex],
            tab: {
                ...tabs[tabIndex].tab,
                name: newName,
                category: newTabs[tabIndex].tab.category === Categories.DEFAULT
                    ? Categories.OVERRIDE : newTabs[tabIndex].tab.category,
            },
        };

        return newTabs;
    },
    toggleTabNameEditing(tabs: TabInfo[], tabId: string) {
        const newTabs: TabInfo[] = [...tabs];
        const tabIndex: number = tabs.findIndex((tabInfo: TabInfo) => tabInfo.tab.id === tabId);
        newTabs[tabIndex] = {
            ...newTabs[tabIndex],
            isTitleEditable: !newTabs[tabIndex].isTitleEditable,
        };
        return newTabs;
    },
    addFilterTab(tabs: TabInfo[], tabId: string, temporalName: string) {
        const newTabs: TabInfo[] = [...tabs];
        const tabIndex: number = tabs.findIndex((tabInfo: TabInfo) => tabInfo.tab.id === tabId);
        newTabs[tabIndex] = {
            ...newTabs[tabIndex],
            tab: {
                ...newTabs[tabIndex].tab,
                filters: [...newTabs[tabIndex].tab.filters, { name: temporalName, tmp: true }],
            },
        };
        return newTabs;
    },
    removeFilterTab(tabs: TabInfo[], tabId: string, filterName: string) {
        const newTabs: TabInfo[] = [...tabs];
        const tabIndex: number = tabs.findIndex((tabInfo: TabInfo) => tabInfo.tab.id === tabId);
        const filterIndex = newTabs[tabIndex].tab.filters.findIndex((filter: Filter) => filter.name === filterName);
        newTabs[tabIndex] = {
            ...newTabs[tabIndex],
            tab: {
                ...newTabs[tabIndex].tab,
                filters: newTabs[tabIndex].tab.filters.filter((filter: Filter, index: number) => index !== filterIndex),
            },
        };
        return newTabs;
    },
    editFilterTab(tabs: TabInfo[], tabId: string, newFilter: any) {
        const newTabs: TabInfo[] = [...tabs];
        const tabIndex: number = tabs.findIndex((tabInfo: TabInfo) => tabInfo.tab.id === tabId);
        if (newFilter.booleanQueryOperator && newFilter.booleanQueryOperator !== 'WHERE') {
            newTabs[tabIndex] = {
                ...newTabs[tabIndex],
                tab: {
                    ...newTabs[tabIndex].tab,
                    booleanQueryOperator: newFilter.booleanQueryOperator,
                },
            };
            if (newFilter.value === undefined) {
                return newTabs;
            }
        }
        if (!newFilter.name && !newFilter.newName) {
            return newTabs;
        }
        const filterIndex = newTabs[tabIndex].tab.filters.findIndex((filter: Filter) => {
            if (newFilter.temporalName) {
                return filter.name === newFilter.temporalName;
            }
            return filter.name === newFilter.name;
        });
        if (filterIndex > -1) {
            const modifiedFilter: Filter = {
                name: newFilter.newName ? newFilter.newName : newFilter.name,
            };
            if (newFilter.temporalName) {
                delete modifiedFilter.tmp;
            }
            if (newFilter.condition) {
                if (newFilter.condition === Conditions.LOWER_THAN) {
                    modifiedFilter.lowerThan = newFilter.value;
                } else if (newFilter.condition === Conditions.GREATER_THAN) {
                    modifiedFilter.greaterThan = newFilter.value;
                } else {
                    modifiedFilter.value = newFilter.value;
                }
            }
            newTabs[tabIndex] = {
                ...newTabs[tabIndex],
                tab: {
                    ...newTabs[tabIndex].tab,
                    category: newTabs[tabIndex].tab.category === Categories.DEFAULT
                        ? Categories.OVERRIDE : newTabs[tabIndex].tab.category,
                    filters: newTabs[tabIndex].tab.filters.map((filter: Filter, index: number) => {
                        if (index === filterIndex) {
                            return modifiedFilter;
                        }
                        return filter;
                    }),
                },
            };
        }
        return newTabs;
    },
    getTabs(tabs: TabInfo[], featureNamespace: FeatureNamespace, isMarksOnlyExam?: boolean) {
        if (tabs && tabs.length) {
            const filteredTabs = tabs.filter((tabInfo: TabInfo) => {
                if (isMarksOnlyExam && tabInfo?.tab?.filters?.some((x: Filter) => x?.name === 'setGrade'
                || x?.name === 'finalGrade'
                || x?.name === 'gradesFromEvaluators')
                ) {
                    return false;
                }
                return true;
            });
            return filteredTabs.map((tabInfo: TabInfo): TabItem => ({
                id: tabInfo.tab.id,
                value: tabInfo.tab.id,
                text: this.getTabName(tabInfo.tab, featureNamespace),
                category: tabInfo.tab.category,
                isTitleEditable: tabInfo.isTitleEditable,
            }));
        }
        return [];
    },
    getTabName(tab: TabData, featureNamespace: FeatureNamespace) {
        return !tab.name && tab.id && isNaN(Number(tab.id))
            ? tl(`${featureNamespace}.defaultFilters.${tab.id}`) : tab.name;
    },
    getTabNameForTabId(tabs: TabInfo[], tabId: string, featureNamespace: FeatureNamespace): string {
        const tabInfo = this.getTabInfoById(tabs, tabId);
        return tabStateBuilder.getTabName(tabInfo.tab, featureNamespace);
    },
    getLocalTabsDefinitions(dataType: string, defaultFilters: string[]): TabInfo[] {
        const appSpecifiedNavigationListService = getAppSpecifiedNavigationListService();
        const defaultTabs = appSpecifiedNavigationListService.getDefaultTabsForDataType(dataType);
        return defaultFilters.filter((filterId: string) => defaultTabs.find((tab) => tab.id === filterId && !tab.footerOnly))
            .map((filterId: string): TabInfo => {
                const tab = defaultTabs.find((tab) => tab.id === filterId);
                return {
                    tab: {
                        ...tab,
                        booleanQueryOperator: 'MUST',
                        filters: tab.filters.length === 0 ? [
                            { name: 'empty0', tmp: true },
                        ] : tab.filters,
                    },
                    isTitleEditable: false,
                };
            });
    },
    getTabInfoById(tabs: TabInfo[], tabId: string) {
        return tabs.find((tabInfo: TabInfo) => tabInfo.tab.id === tabId);
    },
};
